import { useContext } from "react";
import { PreviewModeContext } from "features/qualityForm/common/Contexts";
import { DefaultLimits } from "constants/DefaultLimits";
import {
    selectScorable,
    scorableSliceActions,
    selectAgentHasRead,
    selectCommentCharLimitDateCheck,
    selectIsCalibration,
    selectIsConversationAgeLimitReached,
    selectReviewComments,
    selectIsScorableRetracted,
    selectIsScorableInReview,
} from "features/scorable/scorableSlice";
import {
    GuxFormFieldTextarea,
    GuxTooltip,
} from "genesys-spark-components-react";
import { useStrings } from "features/i18n/useStrings";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import strings from "./footer.strings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import "./OverallComments.less";
import { CommentViewer } from "features/scorable/CommentViewer";
import { RemainingCharacters } from "features/qualityForm/common/RemainingCharacters";
export default function OverallComments({
    disableEditing,
    disableField,
}: {
    disableEditing: boolean;
    disableField?: boolean;
}) {
    const dispatch = useAppDispatch();
    const { previewMode } = useContext(PreviewModeContext);
    const overallComments = useAppSelector((state) => {
        return selectScorable(state)?.answers?.comments;
    });
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const isEvaluationAssigneeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
    });
    const t = useStrings(strings);
    const commentCharLimitDateCheck = useAppSelector(
        selectCommentCharLimitDateCheck
    );
    const enforceCommentLimit = previewMode || commentCharLimitDateCheck;
    const hasError =
        enforceCommentLimit &&
        overallComments &&
        overallComments.length > DefaultLimits.generalCommentCharLimit;

    return (
        <>
            {!disableEditing && (
                <div
                    className="OverallCommentsOpen"
                    data-testid="comments-box"
                    data-invalid={hasError === true}
                >
                    <GuxFormFieldTextarea resize="auto">
                        <textarea
                            slot="input"
                            name="textarea"
                            placeholder={t.labels.addComments}
                            onChange={({ currentTarget }) =>
                                dispatch(
                                    scorableSliceActions.fillEvaluatorComments(
                                        currentTarget.value
                                    )
                                )
                            }
                            defaultValue={overallComments}
                            disabled={disableField}
                        ></textarea>
                        <label slot="label">{t.labels.overallComments}</label>
                        {hasError && (
                            <span slot="error">
                                {t.labels.commentCharLimitExceeded({
                                    characterLimit:
                                        DefaultLimits.generalCommentCharLimit,
                                })}
                            </span>
                        )}
                    </GuxFormFieldTextarea>
                    {enforceCommentLimit && (
                        <RemainingCharacters
                            currentCount={overallComments?.length ?? 0}
                            totalCount={DefaultLimits.generalCommentCharLimit}
                        />
                    )}
                </div>
            )}
            {overallComments && disableEditing && (
                <CommentViewer
                    label={
                        isEvaluationAssigneeFeatureEnabled && isNotCalibration
                            ? t.labels.evaluatorComments
                            : t.labels.overallComments
                    }
                    comment={overallComments ?? ""}
                />
            )}
        </>
    );
}
export function OverallAgentComments({ context }: { context: string }) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const agentComments = useAppSelector(selectReviewComments);
    const isAdmin = context === "admin";
    const { previewMode } = useContext(PreviewModeContext);
    const hasAgentReviewed = useAppSelector(selectAgentHasRead) ?? false;
    const showAgentComments =
        (agentComments ?? "").trim().length > 0 || !isAdmin;
    const isStatusRetracted = useAppSelector(selectIsScorableRetracted);
    const isStatusInReview = useAppSelector(selectIsScorableInReview);
    const allowEditing =
        !hasAgentReviewed &&
        !isAdmin &&
        !isStatusRetracted &&
        !isStatusInReview;
    const commentCharLimitDateCheck = useAppSelector(
        selectCommentCharLimitDateCheck
    );
    const enforceCommentLimit = previewMode || commentCharLimitDateCheck;
    const hasError =
        enforceCommentLimit &&
        agentComments &&
        agentComments.length > DefaultLimits.generalCommentCharLimit;
    const hasAgentReviewExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.agentReviewExpiry
        );
    });
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const isEvaluationAssigneeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
    });
    if (!showAgentComments) {
        return null;
    }
    return (
        <>
            {allowEditing ? (
                <div
                    className="OverallAgentCommentsOpen"
                    data-testid="comments-box"
                >
                    <GuxFormFieldTextarea resize="auto">
                        <textarea
                            slot="input"
                            value={agentComments ?? ""}
                            disabled={
                                isAdmin ||
                                hasAgentReviewed ||
                                hasAgentReviewExpired
                            }
                            aria-disabled={
                                isAdmin ||
                                hasAgentReviewed ||
                                hasAgentReviewExpired
                            }
                            onChange={({ currentTarget }) => {
                                dispatch(
                                    scorableSliceActions.fillReviewComments(
                                        currentTarget.value
                                    )
                                );
                            }}
                        ></textarea>
                        <label slot="label" data-testid="label-comment-section">
                            {isEvaluationAssigneeFeatureEnabled &&
                            isNotCalibration
                                ? t.labels.reviewComments
                                : t.labels.agentComments}
                        </label>
                        {hasError && (
                            <span slot="error">
                                {t.labels.commentCharLimitExceeded({
                                    characterLimit:
                                        DefaultLimits.generalCommentCharLimit,
                                })}
                            </span>
                        )}
                    </GuxFormFieldTextarea>
                    {enforceCommentLimit && (
                        <RemainingCharacters
                            currentCount={agentComments?.length}
                            totalCount={DefaultLimits.generalCommentCharLimit}
                        />
                    )}
                    {hasAgentReviewExpired && (
                        <GuxTooltip>
                            {t.labels.agentReviewExpired({
                                agentReviewExpiry:
                                    DefaultLimits.agentReviewExpiry,
                            })}
                        </GuxTooltip>
                    )}
                </div>
            ) : (
                <>
                    {agentComments && (
                        <CommentViewer
                            label={
                                isEvaluationAssigneeFeatureEnabled &&
                                isNotCalibration
                                    ? t.labels.reviewComments
                                    : t.labels.agentComments
                            }
                            comment={agentComments ?? ""}
                        />
                    )}
                </>
            )}
        </>
    );
}
